import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  signal,
  WritableSignal,
  effect,
  Signal,
  computed,
} from '@angular/core';
import { Config } from '@environments/config';
import { Experiences } from '@local/client-contracts';
import { Constants } from '@local/common';
import { UiIconModel } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AssistantsService } from '@shared/services/assistants.service';
import { FlagsService } from '@shared/services/flags.service';
import { cloneDeep, isEmpty, isEqual } from 'lodash';
import { assistantContent } from '../../helpers/assistant.content';
import { PreferenceItem } from '../../models/assistant-preferences.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { AssistantLlmsResponseItem } from '@local/client-contracts/src/assistants';
import { map } from 'rxjs';

export interface AIModel {
  id: string;
  name: string;
  type: string;
  isLatest: boolean;
  icon: string;
}

export interface AIViewModel {
  label: string;
  value: string;
  icon: UiIconModel;
  isLatest: boolean;
  provider: string;
}

interface AssistantsKnowledgeOptions {
  knowledgeBase: PreferenceItem[];
  baseAIModel: PreferenceItem[];
}

@UntilDestroy()
@Component({
  selector: 'assistant-knowledge',
  templateUrl: './assistant-knowledge.component.html',
  styleUrls: ['./assistant-knowledge.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssistantKnowledgeComponent implements OnInit {
  readonly ASSISTANT_KNOWLEDGE_CONTENT = assistantContent.knowledge;
  disableExternalChat: boolean;
  assistantsKnowledgeOptions: WritableSignal<AssistantsKnowledgeOptions> = signal({
    knowledgeBase: this.ASSISTANT_KNOWLEDGE_CONTENT?.knowledgeBase?.dropdownOptions || [],
    baseAIModel: [],
  });

  selectedKnowledgeType: WritableSignal<PreferenceItem> = signal(null);
  selectedBaseAIModelId: WritableSignal<string> = signal(null);
  aiModels: Signal<AssistantLlmsResponseItem[]>;
  baseAIModel: Signal<AIViewModel[]> = computed(() =>
    this.aiModels()?.map(({ name, id, icon, isLatest, provider }) => ({
      label: name,
      value: id,
      icon: { type: 'img', value: { lightUrl: `${Config.chatIconsBaseUrl}${icon}` } } as UiIconModel,
      isLatest,
      provider,
    }))
  );

  defaultAiModel = computed<AssistantLlmsResponseItem>(() => {
    return this.aiModels()?.find(({ type, isLatest }) => isLatest && type === 'gpt') || this.baseAIModel?.[0];
  });

  selectedBaseAIModelOption = computed(() => this.baseAIModel()?.find((llm) => llm.value === this.selectedBaseAIModelId()));
  selectedBaseAIModel = computed(() => this.aiModels()?.find((llm) => llm.id === this.selectedBaseAIModelId()));

  showTools = computed<boolean>(() => {
    return this.selectedKnowledgeType()?.value === 'External' && !!this.assistantProperties().experienceTools;
  });

  showWebSearch = computed<boolean>(() => {
    return this.selectedBaseAIModel()?.features?.includes('web_search');
  });

  showFileSearch = computed<boolean>(() => {
    return this.selectedBaseAIModel()?.features?.includes('file_search');
  });

  assistantId: string;
  readonly: boolean;
  isViewer: boolean;
  assistantProperties: WritableSignal<Experiences.ExperienceGeneralSettings> = signal(null);

  @Input() set assistant(val: Experiences.ExperienceItem<'general'>) {
    const assistantChanged = !this.assistantId || val?.id !== this.assistantId;
    if (assistantChanged || !isEqual(val?.settings, this.assistantProperties())) {
      this.assistantId = val.id;
      this.readonly = val.readOnly;
      this.isViewer = val.permissionRole === 'viewer';

      if (val.settings?.general) {
        this.assistantProperties.set(cloneDeep(val.settings || {}));
        this.initSelectedKnowledgeType();
        this.initSelectedBaseAIModel();
      }
    }
  }

  @Output() updateProperties = new EventEmitter<Experiences.ExperienceGeneralSettings>();
  @Output() updateDisplayDataSource = new EventEmitter<boolean>();
  @Output() updateAssistantModel = new EventEmitter<AIViewModel>();

  constructor(
    private assistantService: AssistantsService,
    private flagsService: FlagsService
  ) {
    effect(() => {
      if (this.selectedKnowledgeType()?.value === Experiences.KnowledgeType.External && this.selectedBaseAIModel()) {
        this.updateAssistantModel.emit(this.selectedBaseAIModelOption());
      } else {
        this.updateAssistantModel.emit(null);
      }
    });

    this.aiModels = toSignal(this.assistantService.llms$.pipe(map((models) => this.sortModelsByTypeAndVersion(models))));
  }

  ngOnInit() {
    this.loadComponent();
  }

  private sortModelsByTypeAndVersion(models: AIModel[]): AssistantLlmsResponseItem[] {
    const typeOrder = ['gpt', 'Claude', 'Llama', 'Mistral'];

    return [...models].sort((a, b) => {
      const typeComparison = typeOrder.indexOf(a.type) - typeOrder.indexOf(b.type);
      if (typeComparison !== 0) return typeComparison;

      if (a.isLatest !== b.isLatest) return b.isLatest ? 1 : -1;

      return a.name.localeCompare(b.name);
    }) as AssistantLlmsResponseItem[];
  }

  private async loadComponent() {
    // Wait for flags and llm to be loaded
    await Promise.all([this.initFlags()]);
    this.initSelectedKnowledgeType();
    this.initSelectedBaseAIModel();
  }

  private initFlags(): Promise<void> {
    return new Promise((resolve) => {
      this.flagsService.all$.pipe(untilDestroyed(this)).subscribe(async (_) => {
        this.disableExternalChat = await this.flagsService.isEnabled(Constants.DISABLE_EXTERNAL_CHAT);
        this.assistantsKnowledgeOptions.update((current) => ({
          ...current,
          knowledgeBase: current.knowledgeBase.map((option) => ({
            ...option,
            disabled: option.value === Experiences.KnowledgeType.External && this.disableExternalChat,
          })),
        }));
        resolve();
      });
    });
  }

  private initSelectedBaseAIModel() {
    const aiModel: string =
      !isEmpty(this.assistantProperties()) && this.assistantProperties()?.model
        ? this.assistantProperties()?.model
        : this.defaultAiModel()?.id;
    this.setSelectedBaseAIModelId(aiModel);
  }

  private initSelectedKnowledgeType() {
    let knowledgeType: Experiences.KnowledgeType = this.assistantProperties()?.general?.knowledgeType;
    if (!knowledgeType && (this.assistantId || (this.disableExternalChat && !this.assistantId))) {
      knowledgeType = Experiences.KnowledgeType.Internal;
    }
    this.updateDisplayDataSource.emit(knowledgeType === Experiences.KnowledgeType.Internal);
    this.setSelectedKnowledgeType(knowledgeType);
    if (this.disableExternalChat && !this.assistantId && !this.assistantProperties()?.general?.knowledgeType) {
      this.updateAssistantProperties({ general: { knowledgeType: this.selectedKnowledgeType()?.value as Experiences.KnowledgeType } });
    }
  }
  private setSelectedKnowledgeType(knowledgeType: Experiences.KnowledgeType) {
    this.selectedKnowledgeType.set(
      this.assistantsKnowledgeOptions()?.knowledgeBase?.find((option: PreferenceItem) => option.value === knowledgeType)
    );
  }
  private setSelectedBaseAIModelId(baseAIModel: string) {
    this.selectedBaseAIModelId.set(baseAIModel);
  }

  onDropDownChanged($event: PreferenceItem, selectedType: string) {
    const selectedPreference: Partial<Experiences.GeneralExtendSettings> & Partial<Experiences.ExperienceGeneralSettings> = {};
    if (selectedType === 'knowledgeType') {
      if ($event.value === Experiences.KnowledgeType.Internal) {
        selectedPreference.model = undefined;
        selectedPreference.experienceTools = this.getExperienceTools();
        this.updateDisplayDataSource.emit(true);
      } else if ($event.value === Experiences.KnowledgeType.External) {
        this.updateDisplayDataSource.emit(false);
        if (!this.assistantProperties()?.model) {
          selectedPreference.model = this.defaultAiModel()?.id;
          selectedPreference.experienceTools = this.getExperienceTools(this.defaultAiModel());
          selectedPreference.provider = this.defaultAiModel()?.provider;
        }
      }
      this.setSelectedKnowledgeType($event.value as Experiences.KnowledgeType);
      selectedPreference.general = {
        knowledgeType: $event.value as Experiences.KnowledgeType,
        displayOnChatPage: true,
      };
      this.updateAssistantProperties(selectedPreference);
    } else if (selectedType === 'llm') {
      selectedPreference.model = $event.value;
      this.setSelectedBaseAIModelId($event.value);
      selectedPreference.provider = this.selectedBaseAIModel().provider;
      selectedPreference.experienceTools = this.getExperienceTools(this.selectedBaseAIModel());
      this.updateAssistantProperties(selectedPreference);
    }
  }

  getExperienceTools(llm?: AssistantLlmsResponseItem) {
    return {
      webSearch: {
        enabled: llm?.features?.includes('web_search'),
      },
      fileSearch: {
        enabled: llm?.features?.includes('file_search'),
      },
    };
  }

  onChangeTools() {
    this.updateAssistantProperties(this.assistantProperties());
  }

  updateAssistantProperties(selectedPreference: Experiences.ExperienceGeneralSettings) {
    const general = {
      ...this.assistantProperties()?.general,
      ...selectedPreference?.general,
    };
    this.assistantProperties.set({
      ...this.assistantProperties(),
      ...selectedPreference,
      general,
    });
    this.updateProperties.emit(this.assistantProperties());
  }
}
